import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../store/slices/authentication";
import { useAskAiStream } from "../../../hooks/useAskAiStream";
import { useAiQuota } from "../../../hooks/useAiQuota";
import { getSessionToken } from "../../../lib/clerk";
import BlockedFeatureBanner from "../../shared/BlockedFeatureBanner";
import SharedCollapsibleIntro from "../../shared/CollapsibleIntro";
import SearchSteps from "./AskAi/SearchSteps";
import StreamingAnswer from "./AskAi/StreamingAnswer";
import { getItem as safeGetItem, setItem as safeSetItem } from "../../../utils/safeStorage";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

const INTRO_TEXT = "text-[17px] font-body text-dupontBlue dark:text-darkText leading-relaxed";

// All suggested questions — 4 shown at a time, cycling every 8 seconds
const ALL_SUGGESTIONS = [
  // Family history
  "Who were the earliest du Ponts in America?",
  "Tell me about the 3rd generation",
  "Are there family members from Delaware?",
  "What happened to the du Pont gunpowder mills?",
  "Who was Eleuthère Irénée du Pont?",
  "Tell me about Pierre Samuel du Pont de Nemours",
  "Which du Ponts served in the military?",
  "How did the du Pont family come to America?",
  // Personal / relationship
  "How am I connected to the founders?",
  "Who are my closest cousins?",
  "Tell me about my branch of the family",
  "What generation am I?",
  // App help
  "How do I use the Cousin Lookup?",
  "How do I search the family tree?",
  "What is the Family Connect Hub?",
  "How do I send a message to a family member?",
];

// FAQ categories — shown in a collapsible guide
const FAQ_CATEGORIES = [
  {
    title: "Family History",
    questions: [
      "Who were the earliest du Ponts in America?",
      "Tell me about the 3rd generation",
      "What happened to the du Pont gunpowder mills?",
      "Who was Pierre Samuel du Pont de Nemours?",
      "Which du Ponts lived in Delaware?",
      "How did the du Pont family come to America from France?",
    ],
  },
  {
    title: "About Me & My Family",
    questions: [
      "How am I connected to the founders?",
      "Who are my closest cousins?",
      "Tell me about my branch of the family",
      "What generation am I?",
      "Do I have any cousins aged 19-29?",
      "Who are my grandparents in the tree?",
    ],
  },
  {
    title: "Relationships & Connections",
    questions: [
      "How are two specific people related?",
      "Who married into the du Pont family?",
      "Which du Ponts had the most children?",
      "Are there any adopted members in the family?",
    ],
  },
  {
    title: "Using This App",
    questions: [
      "How do I use the Cousin Lookup?",
      "How do I search the family tree?",
      "What is the Family Connect Hub?",
      "How do I send a message to a family member?",
      "How do I post on the Bulletin Board?",
      "What is the Marketplace for?",
    ],
  },
];

// Memoized completed chat pair — never re-renders once the answer is set
const ChatPair = React.memo(function ChatPair({ pair }) {
  return (
    <div className="flex flex-col gap-y-[12px]">
      <div className="flex justify-end">
        <div className="max-w-[85%] sm:max-w-[70%] bg-[#00091c] dark:bg-[#1a2744] text-white px-[16px] py-[10px] rounded-[16px] rounded-br-[4px] text-[15px] font-body whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
          {pair.question}
        </div>
      </div>
      {pair.answer ? (
        <div className="flex justify-start">
          <div className="max-w-[85%] sm:max-w-[80%] bg-dupontCream dark:bg-darkCard border border-dupontGold/20 dark:border-darkGold/20 rounded-[16px] rounded-bl-[4px] px-[16px] py-[12px]">
            <SearchSteps steps={pair.searchSteps} isSearching={false} />
            {/* Same size as while it was being written (StreamingAnswer), so
                the answer doesn't shrink the moment it finishes. */}
            <div className="font-body text-[17px] leading-relaxed text-dupontBlue dark:text-darkText whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
              {pair.answer}
            </div>
            {pair.matchedCount > 0 && (
              <p className="text-[13px] font-body text-dupontBlue/40 dark:text-darkText/40 mt-[8px] pt-[8px] border-t border-dupontGold/10 dark:border-darkGold/10">
                Based on {pair.matchedCount} record{pair.matchedCount !== 1 ? "s" : ""} from the family database
              </p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
});

function AskAi() {
  const user = useSelector(selectUser);
  const individualId = user?.externalId ? parseInt(user.externalId) : null;

  // Chat history (session only — resets on navigation/refresh)
  const [chatPairs, setChatPairs] = useState([]);
  const [suggestionPage, setSuggestionPage] = useState(
    () => Math.floor(Math.random() * Math.ceil(ALL_SUGGESTIONS.length / 4))
  );
  const [faqOpen, setFaqOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  // "Request more questions" flow: idle → open (form shown) → sending → sent / error
  const [requestState, setRequestState] = useState("idle");
  const requestMsgRef = useRef(null);
  const [noticeDismissed, setNoticeDismissed] = useState(
    () => safeGetItem("askAiNoticeDismissed") === "1"
  );

  const dismissNotice = () => {
    safeSetItem("askAiNoticeDismissed", "1");
    setNoticeDismissed(true);
  };
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const {
    searchSteps,
    answerText,
    status,
    matchedCount,
    errorMessage,
    askQuestion,
    cancel,
  } = useAskAiStream();

  // Weekly question allowance (7/week, resets Monday). Only successful
  // answers count, so we re-check with the server after each one finishes.
  const { quota, refresh: refreshQuota } = useAiQuota();
  const remaining = quota ? quota.remaining : null;
  const locked = quota ? quota.remaining <= 0 : false;

  const totalPages = Math.ceil(ALL_SUGGESTIONS.length / 4);

  // Get current 4 suggestions
  const currentSuggestions = ALL_SUGGESTIONS.slice(
    suggestionPage * 4,
    suggestionPage * 4 + 4
  );

  // Build multi-turn history from past chat pairs
  const buildHistory = useCallback((pairs = chatPairs) => {
    const history = [];
    for (const pair of pairs) {
      history.push({ role: "user", content: pair.question });
      if (pair.answer) {
        history.push({ role: "assistant", content: pair.answer });
      }
    }
    return history;
  }, [chatPairs]);

  // When the AI finishes answering, save this Q&A pair to history
  useEffect(() => {
    if (status === "done" && answerText) {
      setChatPairs((prev) => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last && !last.answer) {
          updated[updated.length - 1] = {
            ...last,
            searchSteps: [...searchSteps],
            answer: answerText,
            matchedCount,
          };
        }
        return updated;
      });
    }
  }, [status, answerText, searchSteps, matchedCount]);

  // Re-check the weekly allowance whenever a question resolves. A successful
  // answer ("done") uses one; an error/limit hit ("error") doesn't, but we
  // refresh anyway to keep the counter in sync with the server.
  useEffect(() => {
    if (status === "done" || status === "error") {
      refreshQuota();
    }
  }, [status, refreshQuota]);

  // Auto-scroll to bottom when new content arrives
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatPairs, searchSteps, answerText, status]);

  const handleSubmit = useCallback(
    (questionText) => {
      const q = (questionText || inputRef.current?.value || "").trim();
      if (!q || status === "searching" || status === "answering") return;
      if (locked) return; // out of questions for the week

      setChatPairs((prev) => [
        ...prev,
        { question: q, searchSteps: [], answer: null, matchedCount: 0 },
      ]);
      if (inputRef.current) {
        inputRef.current.value = "";
        inputRef.current.style.height = "60px";
      }
      setFaqOpen(false);

      const history = buildHistory();
      askQuestion(q, individualId, history);
    },
    [status, locked, buildHistory, askQuestion, individualId]
  );

  // The last question, when it is still waiting for its answer.
  const unanswered = chatPairs.length > 0 && !chatPairs[chatPairs.length - 1].answer
    ? chatPairs[chatPairs.length - 1]
    : null;

  // Ask the unanswered question again, in the same place in the conversation.
  const handleRetry = useCallback(() => {
    if (!unanswered || status === "searching" || status === "answering" || locked) return;
    askQuestion(unanswered.question, individualId, buildHistory(chatPairs.slice(0, -1)));
  }, [unanswered, status, locked, askQuestion, individualId, buildHistory, chatPairs]);

  // Stop waiting: the question goes back into the box, ready to change or ask again.
  const handleStop = useCallback(() => {
    cancel();
    if (!unanswered) return;
    setChatPairs((prev) => prev.slice(0, -1));
    if (inputRef.current) {
      inputRef.current.value = unanswered.question;
      // After this render, once the box is no longer disabled.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [cancel, unanswered]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Send a "request more questions" note to the family admins' notification center.
  const submitMoreQuestionsRequest = useCallback(async () => {
    setRequestState("sending");
    try {
      const token = await getSessionToken();
      const note = (requestMsgRef.current?.value || "").trim();
      const res = await fetch(`${API_URL}/notifications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          category: "more_questions",
          message: note || "I've used my questions for the week and would like a few more.",
        }),
      });
      setRequestState(res.ok ? "sent" : "error");
    } catch {
      setRequestState("error");
    }
  }, []);

  const isActive = status === "searching" || status === "answering";
  const isEmpty = chatPairs.length === 0 && !isActive;

  return (
    <BlockedFeatureBanner
      featureKey="is_ai_blocked"
      featureName="Ask AI"
      individualId={individualId}
      mode="full"
    >
    <div className="flex flex-col grow max-w-[800px] mx-auto w-full">
      {/* Header */}
      <div className="flex flex-col items-center mb-[16px]">
        <h2 className="text-[24px] font-display font-bold text-dupontBlue dark:text-darkText">
          Ask the Family Historian
        </h2>
        <div className="w-[60px] h-[1px] bg-dupontGold mt-[8px]" />

        {/* Weekly question counter — each dot is one question. Filled gold =
            still available, hollow = already used. Sized large for readability. */}
        {quota && (
          <div className="flex flex-col items-center gap-y-[10px] mt-[18px]">
            <div className="flex gap-x-[10px]" aria-hidden="true">
              {Array.from({ length: quota.limit }).map((_, i) => (
                <span
                  key={i}
                  className={`w-[18px] h-[18px] rounded-full border-2 transition-colors duration-300
                    ${
                      i < remaining
                        ? "bg-dupontGold border-dupontGold dark:bg-darkGold dark:border-darkGold"
                        : "bg-transparent border-dupontGold/40 dark:border-darkGold/40"
                    }`}
                />
              ))}
            </div>
            <p className="text-[18px] font-body text-dupontBlue dark:text-darkText">
              {remaining > 0 ? (
                <>
                  <span className="font-bold">
                    {remaining} of {quota.limit}
                  </span>{" "}
                  question{remaining !== 1 ? "s" : ""} left this week
                  <span className="text-dupontBlue/60 dark:text-darkGold">
                    {" "}· refills Monday
                  </span>
                </>
              ) : (
                <span className="font-bold">
                  No questions left this week — refills Monday
                </span>
              )}
            </p>
          </div>
        )}
      </div>

      {/* Sam's landing copy (approved 2026-09-27), collapsed the way the
          Bulletin and Marketplace show theirs: under the title and the weekly
          counter, above the conversation and the question box. */}
      <div className="mb-[20px]">
        <SharedCollapsibleIntro
          heading="Your Guide to the Family Tree"
          teaser="Relationships, ancestors, and help finding your way around the site. Type your question in the box below."
        >
          <p className={INTRO_TEXT}>
            {"The family tree is big. Thousands of names, generations of marriages, branches that cross over each other more than once. Ask AI is here so you do not have to read all of it to find what you are looking for."}
          </p>
          <p className={INTRO_TEXT}>
            {"Ask it how you are related to someone, who your great-grandparents were, or where something lives on the site. It answers from our own family tree, and it will tell you when it does not know. There is a weekly limit on questions so it stays affordable for everyone."}
          </p>
          <p className={INTRO_TEXT}>
            {"And if a cousin cannot get signed in, ask it that too. It knows where the code emails like to hide."}
          </p>
        </SharedCollapsibleIntro>
      </div>

      {/* Plain-language notice explaining how the weekly questions work.
          Styled to match the Correspondence notice; dismissible with "Got it". */}
      {!noticeDismissed && (
        <div className="flex flex-col gap-y-[14px] p-[24px] mb-[20px] rounded-[12px] border-2 border-dupontGold/40 bg-dupontGold/10 dark:bg-dupontGold/5">
          <h3 className="text-[20px] font-display font-bold text-dupontBlue dark:text-dupontGold">
            How your questions work
          </h3>
          <ul className="flex flex-col gap-y-[10px] text-[18px] font-body text-dupontBlue dark:text-darkText leading-relaxed">
            <li>
              • You get <span className="font-bold">7 questions each week</span> — about one a day —
              to ask the Family Historian.
            </li>
            <li>
              • Your questions <span className="font-bold">refill every Monday</span>.
            </li>
            <li>
              • A question only counts <span className="font-bold">when you get an answer</span>. If
              something goes wrong, it won't be counted against you.
            </li>
            <li>
              • When you've used all 7, you can <span className="font-bold">request more</span> from
              the family admins.
            </li>
          </ul>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={dismissNotice}
              className="px-[20px] py-[8px] text-[15px] font-display font-semibold rounded-full transition-colors duration-200 bg-dupontGold text-white hover:bg-dupontGold/90 cursor-pointer"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Chat area — defined panel so the conversation reads as its own space */}
      <div className="flex flex-col grow overflow-y-auto min-h-0 px-[20px] py-[16px] gap-y-[20px]
        rounded-[16px] border border-dupontGold/30 dark:border-darkGold/25
        bg-white/60 dark:bg-darkCard/40 shadow-sm">
        {/* Empty state — suggestions + FAQ */}
        {isEmpty && (
          <div className="flex flex-col items-center gap-y-[20px] py-[24px]">
            <p className="text-[19px] font-body font-medium text-dupontBlue dark:text-darkText text-center">
              Ask me anything about du Pont family history, or try one of these:
            </p>

            {/* Cycling suggestions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] w-full max-w-[600px]">
              {currentSuggestions.map((q, i) => (
                <button
                  key={`${suggestionPage}-${i}`}
                  onClick={() => handleSubmit(q)}
                  className="text-left text-[17px] font-body font-medium p-[14px] rounded-[8px]
                    border border-dupontGold/30 dark:border-darkGold/30
                    bg-dupontCream dark:bg-darkCard
                    text-dupontBlue dark:text-gray-100
                    hover:border-dupontGold hover:bg-dupontGold/5 dark:hover:border-darkGold dark:hover:bg-darkGold/5
                    transition-all cursor-pointer animate-fadeIn"
                >
                  {q}
                </button>
              ))}
            </div>

            {/* Page dots */}
            <div className="flex gap-x-[10px]">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setSuggestionPage(i)}
                  className={`w-[10px] h-[10px] rounded-full transition-all duration-200 cursor-pointer
                    hover:scale-125
                    ${
                      i === suggestionPage
                        ? "bg-dupontGold dark:bg-darkGold scale-110"
                        : "bg-dupontGold/25 dark:bg-darkGold/25 hover:bg-dupontGold/50 dark:hover:bg-darkGold/50"
                    }`}
                />
              ))}
            </div>

            {/* FAQ Guide — collapsible */}
            <div className="w-full max-w-[600px] mt-[8px]">
              <button
                onClick={() => setFaqOpen(!faqOpen)}
                className="w-full flex items-center justify-between px-[18px] py-[12px]
                  rounded-[8px] border border-dupontGold/20 dark:border-darkGold/20
                  text-[17px] font-body font-semibold text-dupontBlue dark:text-darkText
                  hover:border-dupontGold/40 dark:hover:border-darkGold/40
                  transition-colors cursor-pointer"
              >
                <span>Not sure what to ask? Browse by topic</span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`text-dupontGold dark:text-darkGold transition-transform ${
                    faqOpen ? "rotate-180" : ""
                  }`}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {faqOpen && (
                <div className="mt-[8px] max-h-[400px] overflow-y-auto rounded-[8px] border border-dupontGold/20 dark:border-darkGold/20 divide-y divide-dupontGold/10 dark:divide-darkGold/10 animate-fadeIn">
                  {FAQ_CATEGORIES.map((cat, ci) => (
                    <div key={ci}>
                      {/* Category header */}
                      <button
                        onClick={() =>
                          setExpandedCategory(expandedCategory === ci ? null : ci)
                        }
                        className="w-full flex items-center justify-between px-[16px] py-[12px]
                          text-[16px] font-body font-bold text-dupontGold dark:text-darkGold
                          hover:bg-dupontGold/5 dark:hover:bg-darkGold/5
                          transition-colors cursor-pointer"
                      >
                        <span>{cat.title}</span>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className={`transition-transform ${
                            expandedCategory === ci ? "rotate-180" : ""
                          }`}
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>

                      {/* Questions list */}
                      {expandedCategory === ci && (
                        <div className="pb-[8px] animate-fadeIn">
                          {cat.questions.map((q, qi) => (
                            <button
                              key={qi}
                              onClick={() => handleSubmit(q)}
                              className="w-full text-left px-[16px] py-[10px] pl-[32px]
                                text-[15px] font-body text-dupontBlue/80 dark:text-darkText/80
                                hover:text-dupontBlue dark:hover:text-darkText
                                hover:bg-dupontGold/5 dark:hover:bg-darkGold/5
                                transition-colors cursor-pointer"
                            >
                              {q}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Completed chat pairs — memoized so they don't re-render when typing */}
        {chatPairs.map((pair, i) => (
          <ChatPair key={i} pair={pair} />
        ))}

        {/* Currently streaming response */}
        {isActive && (
          <div className="flex flex-col gap-y-[12px]">
            <div className="flex justify-start">
              <div className="max-w-[85%] sm:max-w-[80%] bg-dupontCream dark:bg-darkCard border border-dupontGold/20 dark:border-darkGold/20 rounded-[16px] rounded-bl-[4px] px-[16px] py-[12px]">
                <SearchSteps steps={searchSteps} isSearching={status === "searching"} />
                {status === "answering" && (
                  <StreamingAnswer text={answerText} isDone={false} />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {status === "error" && errorMessage && (
          <div className="flex justify-start">
            <div className="max-w-[85%] sm:max-w-[80%] bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-[16px] rounded-bl-[4px] px-[16px] py-[12px]">
              <p className="text-[14px] font-body text-red-700 dark:text-red-300 break-words [overflow-wrap:anywhere]">
                {errorMessage}
              </p>
              {unanswered && !locked && (
                <button
                  type="button"
                  onClick={handleRetry}
                  className="mt-[6px] text-[14px] font-body font-semibold text-dupontGold dark:text-darkGold underline cursor-pointer"
                >
                  Try again
                </button>
              )}
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Input area */}
      <div className="mt-auto pt-[20px] pb-[8px] bg-dupontCream dark:bg-darkPrimary border-t border-dupontGold/20 dark:border-darkGold/20">
        {/* Locked panel — shown once the week's 7 questions are used up */}
        {locked && (
          <div className="mb-[14px] rounded-[12px] border border-dupontGold/30 dark:border-darkGold/30 bg-dupontGold/5 dark:bg-darkGold/5 px-[18px] py-[16px] text-center animate-fadeIn">
            <p className="text-[20px] font-display font-semibold text-dupontBlue/90 dark:text-dupontGold/90">
              You've used all {quota?.limit ?? 7} of your questions this week
            </p>
            <p className="text-[18px] font-body text-dupontBlue dark:text-darkText mt-[8px] leading-relaxed">
              They refill Monday. Need more sooner? Let the family admins know.
            </p>
            {requestState === "idle" && (
              <button
                onClick={() => setRequestState("open")}
                className="mt-[12px] px-[20px] py-[10px] rounded-[12px] font-body text-[15px] font-semibold
                  bg-dupontGold dark:bg-darkGold text-white
                  hover:bg-dupontGold/90 dark:hover:bg-darkGold/90
                  shadow-sm transition-colors cursor-pointer"
              >
                Request more questions
              </button>
            )}

            {(requestState === "open" || requestState === "sending") && (
              <div className="mt-[12px] flex flex-col items-stretch gap-y-[10px] max-w-[440px] mx-auto animate-fadeIn">
                <textarea
                  ref={requestMsgRef}
                  rows={2}
                  placeholder="Optional: add a note for the admins…"
                  className="resize-none text-[16px] font-body px-[14px] py-[10px] rounded-[10px]
                    border border-dupontGold/40 dark:border-darkGold/40
                    bg-white dark:bg-darkCard text-dupontBlue dark:text-darkText
                    placeholder:text-dupontBlue/40 dark:placeholder:text-gray-400
                    focus:outline-none focus:border-dupontGold dark:focus:border-darkGold transition-colors"
                />
                <button
                  onClick={submitMoreQuestionsRequest}
                  disabled={requestState === "sending"}
                  className="px-[20px] py-[10px] rounded-[12px] font-body text-[16px] font-semibold
                    bg-dupontGold dark:bg-darkGold text-white
                    hover:bg-dupontGold/90 dark:hover:bg-darkGold/90
                    disabled:opacity-50 disabled:cursor-not-allowed
                    shadow-sm transition-colors cursor-pointer"
                >
                  {requestState === "sending" ? "Sending…" : "Send request to admins"}
                </button>
              </div>
            )}

            {requestState === "sent" && (
              <p className="mt-[12px] text-[17px] font-body font-semibold text-green-700 dark:text-green-400">
                ✓ Your request was sent to the family admins.
              </p>
            )}

            {requestState === "error" && (
              <div className="mt-[12px]">
                <p className="text-[16px] font-body text-red-700 dark:text-red-300">
                  Sorry — that didn't send. Please try again.
                </p>
                <button
                  onClick={() => setRequestState("open")}
                  className="mt-[6px] text-[14px] font-body font-semibold text-dupontGold dark:text-darkGold underline cursor-pointer"
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        )}
        <div className="flex items-end gap-x-[12px]">
          <textarea
            ref={inputRef}
            defaultValue=""
            onInput={(e) => {
              // Auto-grow only — no React state updates, so typing is instant
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px";
            }}
            onKeyDown={handleKeyDown}
            placeholder={
              locked
                ? "You're out of questions until Monday."
                : "Ask about family history, relationships, or how to use the app..."
            }
            rows={2}
            className="grow resize-none text-[16px] font-body px-[18px] py-[14px] rounded-[14px]
              border-2 border-dupontGold/40 dark:border-darkGold/40
              bg-white dark:bg-darkCard
              text-dupontBlue dark:text-darkText
              placeholder:text-dupontBlue/50 dark:placeholder:text-gray-400
              focus:outline-none focus:border-dupontGold dark:focus:border-darkGold
              shadow-sm dark:shadow-md
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors"
            style={{ minHeight: "60px", maxHeight: "200px" }}
            disabled={isActive || locked}
          />
          {isActive && (
            <button
              type="button"
              onClick={handleStop}
              className="shrink-0 h-[50px] px-[18px] rounded-[14px] font-body text-[16px] font-semibold
                border-2 border-dupontGold/60 dark:border-darkGold/60
                text-dupontBlue dark:text-darkText bg-white dark:bg-darkCard
                hover:border-dupontGold dark:hover:border-darkGold
                shadow-sm transition-colors cursor-pointer"
            >
              Stop
            </button>
          )}
          <button
            onClick={() => handleSubmit()}
            disabled={isActive || locked}
            className="shrink-0 h-[50px] px-[24px] rounded-[14px] font-body text-[16px] font-semibold
              bg-dupontGold dark:bg-darkGold text-white
              hover:bg-dupontGold/90 dark:hover:bg-darkGold/90
              disabled:opacity-40 disabled:cursor-not-allowed
              shadow-sm transition-colors cursor-pointer"
          >
            {isActive ? "Thinking..." : locked ? "Locked" : "Ask"}
          </button>
        </div>
      </div>
    </div>
    </BlockedFeatureBanner>
  );
}

export default AskAi;
